import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { 
  FileCheck, RefreshCw, Upload, Search, X, 
  ChevronDown, ChevronRight, ChevronLeft, ShieldCheck, 
  ShieldAlert, MinusCircle, Briefcase, Sliders, Plus, Trash2,
  Edit2, ChevronLeft as PageLeft, ChevronRight as PageRight
} from 'lucide-react';

export default function AdminDocuments() {
  const navigate = useNavigate();
  const [matrixData, setMatrixData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [expandedUsers, setExpandedUsers] = useState({});

  // Sayfalama (Pagination)
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Kural Yönetimi Modal State'leri
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [rulesData, setRulesData] = useState({ positions: [], rules: [], documentTypes: [] });
  const [selectedPosition, setSelectedPosition] = useState('');
  const [selectedDocType, setSelectedDocType] = useState('');
  const [rulesSearch, setRulesSearch] = useState('');

  // Excel Aktarım State'leri
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  // Manuel Belge Ekleme / Düzenleme Modal State'leri
  const [showDocModal, setShowDocModal] = useState(false);
  const [docModalMode, setDocModalMode] = useState('add'); // 'add' | 'edit'
  const [activeEmployee, setActiveEmployee] = useState(null);
  const [editingDocId, setEditingDocId] = useState(null);
  const [docFormData, setDocFormData] = useState({
    document_type_id: '',
    document_number: '',
    issue_date: '',
    expiry_date: '',
    notes: ''
  });
  const [docFormLoading, setDocFormLoading] = useState(false);

  const fetchMatrix = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/documents/employee-matrix', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMatrixData(data);
      }
    } catch (err) {
      console.error('Matris verisi alınamadı:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRules = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/documents/rules/positions', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRulesData(data);
        if (data.positions.length > 0 && !selectedPosition) {
          setSelectedPosition(data.positions[0]);
        }
      }
    } catch (err) {
      console.error('Kurallar alınamadı:', err);
    }
  };

  useEffect(() => {
    fetchMatrix();
    fetchRules();
  }, []);

  const handleOpenRules = () => {
    setShowRulesModal(true);
    fetchRules();
  };

  const handleAddRule = async () => {
    if (!selectedPosition || !selectedDocType) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/documents/rules', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({
          position_title: selectedPosition,
          document_type_id: selectedDocType
        })
      });
      if (res.ok) {
        setSelectedDocType('');
        await fetchRules();
        fetchMatrix();
      }
    } catch (err) {
      alert('Kural eklenemedi.');
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Bu yetkinlik kuralını silmek istediğinize emin misiniz?')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/documents/rules/${ruleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        await fetchRules();
        fetchMatrix();
      }
    } catch (err) {
      alert('Kural silinemedi.');
    }
  };

  // Excel Yükleme İşlemleri
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);

    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('token');

    try {
      const res = await fetch('/api/documents/import/preview', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (data.success || res.ok) {
        setPreviewData(data);
        setShowImportModal(true);
      } else {
        alert(data.error || 'Önizleme oluşturulamadı.');
      }
    } catch (err) {
      alert('Dosya gönderilemedi.');
    } finally {
      e.target.value = '';
    }
  };

  const handleExecuteImport = async () => {
    if (!selectedFile) return;
    setIsImporting(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    const token = localStorage.getItem('token');

    try {
      const res = await fetch('/api/documents/import', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        alert(`Sunucu yanıt hatası (HTTP ${res.status}).`);
        return;
      }

      const data = await res.json();
      if (res.ok && !data.error) {
        alert(`Aktarım tamamlandı! ${data.insertedCount || 0} yeni belge eklendi, ${data.updatedCount || 0} güncellendi.`);
        setShowImportModal(false);
        setPreviewData(null);
        setSelectedFile(null);
        fetchMatrix();
      } else {
        alert(data.error || 'Aktarım sırasında bir hata meydana geldi.');
      }
    } catch (err) {
      alert('İstek gönderilemedi: ' + err.message);
    } finally {
      setIsImporting(false);
    }
  };

  // Manuel Belge Ekleme / Düzenleme
  const handleOpenAddDoc = (emp) => {
    setActiveEmployee(emp);
    setDocModalMode('add');
    setEditingDocId(null);
    setDocFormData({
      document_type_id: '',
      document_number: '',
      issue_date: new Date().toISOString().slice(0, 10),
      expiry_date: '',
      notes: ''
    });
    setShowDocModal(true);
  };

  const handleOpenEditDoc = (emp, doc) => {
    setActiveEmployee(emp);
    setDocModalMode('edit');
    setEditingDocId(doc.id);
    setDocFormData({
      document_type_id: doc.document_type_id,
      document_number: doc.document_number || '',
      issue_date: doc.issue_date ? new Date(doc.issue_date).toISOString().slice(0, 10) : '',
      expiry_date: doc.expiry_date ? new Date(doc.expiry_date).toISOString().slice(0, 10) : '',
      notes: doc.notes || ''
    });
    setShowDocModal(true);
  };

  const handleSaveDoc = async (e) => {
    e.preventDefault();
    if (!docFormData.document_type_id || !docFormData.issue_date) {
      alert('Lütfen belge türü ve başlangıç tarihini girin.');
      return;
    }

    setDocFormLoading(true);
    const token = localStorage.getItem('token');

    try {
      const url = docModalMode === 'add' ? '/api/documents' : `/api/documents/${editingDocId}`;
      const method = docModalMode === 'add' ? 'POST' : 'PUT';

      const payload = {
        ...docFormData,
        user_id: activeEmployee.user_id,
        expiry_date: docFormData.expiry_date || null
      };

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (res.ok && !data.error) {
        setShowDocModal(false);
        fetchMatrix();
      } else {
        alert(data.error || 'İşlem başarısız oldu.');
      }
    } catch (err) {
      alert('İstek gönderilemedi: ' + err.message);
    } finally {
      setDocFormLoading(false);
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Bu belge kaydını silmek istediğinize emin misiniz?')) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`/api/documents/${docId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchMatrix();
      } else {
        alert('Belge silinemedi.');
      }
    } catch (err) {
      alert('Silme isteği başarısız oldu.');
    }
  };

  const toggleUser = (userId) => {
    setExpandedUsers(prev => ({ ...prev, [userId]: !prev[userId] }));
  };

  // Filtreleme
  const filteredMatrix = matrixData.filter(emp => {
    const term = searchTerm.toLowerCase();
    const name = String(emp.employee_name || '').toLowerCase();
    const sicil = String(emp.sicil || '').toLowerCase();
    const title = String(emp.user_title || '').toLowerCase();
    const matchTerm = name.includes(term) || sicil.includes(term) || title.includes(term);

    if (statusFilter === 'COMPLIANT') return matchTerm && emp.complianceStatus === 'compliant';
    if (statusFilter === 'MISSING') return matchTerm && emp.complianceStatus === 'missing';
    if (statusFilter === 'EXEMPT') return matchTerm && emp.complianceStatus === 'exempt';
    return matchTerm;
  });

  // Sayfalama
  const totalItems = filteredMatrix.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredMatrix.slice(startIndex, startIndex + itemsPerPage);

  // Kural arama filtresi
  const filteredAllRules = (rulesData.rules || []).filter(r => {
    const term = rulesSearch.toLowerCase();
    const pos = String(r.position_title || '').toLowerCase();
    const doc = String(r.document_name || '').toLowerCase();
    return pos.includes(term) || doc.includes(term);
  });

  return (
    <AdminLayout>
      <div style={{ padding: '24px', width: '100%', margin: '0' }}>
        
        {/* Üst Başlık ve Aksiyonlar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
              <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }} title="Geri">
                <ChevronLeft size={28} color="#334155" />
              </button>
              <FileCheck size={28} color="#e11d48" /> Belge & Yetkinlik Takibi
            </h1>
            <p style={{ color: '#64748b', margin: '4px 0 0 36px', fontSize: '14px' }}>
              Pozisyon kuralları, çalışan yetkinlik uyumluluğu ve belge yönetimi.
            </p>
          </div>
          
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              onClick={handleOpenRules}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 16px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '13px' }}
            >
              <Sliders size={16} /> Yetkinlik Kuralları ({rulesData.rules?.length || 0})
            </button>
            <button 
              onClick={fetchMatrix} 
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 16px', backgroundColor: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1', borderRadius: '8px', fontWeight: '500', cursor: 'pointer', fontSize: '13px' }}
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Yenile
            </button>
            <button 
              onClick={() => fileInputRef.current?.click()} 
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 18px', backgroundColor: '#e11d48', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '13px' }}
            >
              <Upload size={16} /> Excel İçe Aktar
            </button>
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleFileChange} 
              accept=".xlsx, .xls" 
              style={{ display: 'none' }} 
            />
          </div>
        </div>

        {/* Liste ve Arama */}
        <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
              <Search size={18} color="#94a3b8" />
              <input
                type="text"
                placeholder="Personel adı, sicil veya görev ara..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                style={{ border: 'none', outline: 'none', width: '100%', fontSize: '14px' }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '6px' }}>
              <button 
                onClick={() => { setStatusFilter('ALL'); setCurrentPage(1); }}
                style={{ padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', border: 'none', cursor: 'pointer', background: statusFilter === 'ALL' ? '#0f172a' : '#f1f5f9', color: statusFilter === 'ALL' ? '#fff' : '#475569' }}
              >
                Tümü ({matrixData.length})
              </button>
              <button 
                onClick={() => { setStatusFilter('COMPLIANT'); setCurrentPage(1); }}
                style={{ padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', border: 'none', cursor: 'pointer', background: statusFilter === 'COMPLIANT' ? '#16a34a' : '#f0fdf4', color: statusFilter === 'COMPLIANT' ? '#fff' : '#15803d' }}
              >
                Yetkin ({matrixData.filter(m => m.complianceStatus === 'compliant').length})
              </button>
              <button 
                onClick={() => { setStatusFilter('MISSING'); setCurrentPage(1); }}
                style={{ padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', border: 'none', cursor: 'pointer', background: statusFilter === 'MISSING' ? '#dc2626' : '#fef2f2', color: statusFilter === 'MISSING' ? '#fff' : '#b91c1c' }}
              >
                Belge Eksik ({matrixData.filter(m => m.complianceStatus === 'missing').length})
              </button>
            </div>
          </div>

          {loading ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#64748b' }}>Yükleniyor...</div>
          ) : filteredMatrix.length === 0 ? (
            <div style={{ padding: '60px 20px', textAlign: 'center', color: '#64748b' }}>Kayıt bulunamadı.</div>
          ) : (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '40px 2fr 2fr 1.5fr 1fr', padding: '12px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                <div></div>
                <div>Çalışan / Sicil</div>
                <div>Görevi / Pozisyon</div>
                <div>Yetkinlik Durumu</div>
                <div style={{ textAlign: 'right' }}>Mevcut Belge</div>
              </div>

              {currentItems.map(emp => {
                const isExpanded = !!expandedUsers[emp.user_id];
                return (
                  <div key={emp.user_id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <div 
                      onClick={() => toggleUser(emp.user_id)}
                      style={{ display: 'grid', gridTemplateColumns: '40px 2fr 2fr 1.5fr 1fr', padding: '16px 20px', alignItems: 'center', cursor: 'pointer', background: isExpanded ? '#fafafa' : '#fff' }}
                    >
                      <div>{isExpanded ? <ChevronDown size={18} color="#64748b" /> : <ChevronRight size={18} color="#94a3b8" />}</div>
                      <div>
                        <div style={{ fontWeight: '600', color: '#1e293b' }}>{emp.employee_name}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>Sicil: {emp.sicil}</div>
                      </div>
                      <div style={{ color: '#334155', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Briefcase size={14} color="#94a3b8" />
                        <span>{emp.user_title || 'Tanımsız Pozisyon'}</span>
                      </div>
                      <div>
                        {emp.complianceStatus === 'compliant' && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', background: '#dcfce7', color: '#15803d' }}>
                            <ShieldCheck size={15} /> Yetkin / Belgeli
                          </span>
                        )}
                        {emp.complianceStatus === 'missing' && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', background: '#fee2e2', color: '#b91c1c' }}>
                            <ShieldAlert size={15} /> {emp.missingDocuments?.length} Belge Eksik
                          </span>
                        )}
                        {emp.complianceStatus === 'exempt' && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '500', background: '#f1f5f9', color: '#64748b' }}>
                            <MinusCircle size={15} /> Belge Şartı Yok
                          </span>
                        )}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', background: '#f1f5f9', color: '#475569' }}>
                          {(emp.documents || []).length} Belge
                        </span>
                      </div>
                    </div>

                    {/* Akordiyon İçi: Çift Kolonlu Belge Paneli */}
                    {isExpanded && (
                      <div style={{ padding: '20px 24px', background: '#f8fafc', borderTop: '1px dashed #e2e8f0', display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '24px' }}>
                        {/* Sol Kolon: Sahip Olduğu Belgeler ve Ekleme/Düzenleme */}
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <span style={{ fontSize: '12px', fontWeight: '700', color: '#334155', textTransform: 'uppercase' }}>
                              Sahip Olduğu Belgeler ({emp.documents?.length || 0})
                            </span>
                            <button 
                              onClick={() => handleOpenAddDoc(emp)}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '5px 10px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                            >
                              <Plus size={14} /> Yeni Belge Ekle
                            </button>
                          </div>

                          {(!emp.documents || emp.documents.length === 0) ? (
                            <div style={{ padding: '14px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', color: '#94a3b8' }}>Kayıtlı herhangi bir belge bulunmuyor.</div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {emp.documents.map((doc, idx) => (
                                <div key={doc.id || idx} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: '600', fontSize: '13px', color: '#1e293b' }}>{doc.document_name}</div>
                                    <div style={{ display: 'flex', gap: '14px', marginTop: '6px', fontSize: '11px', color: '#64748b' }}>
                                      <span>📅 Başlangıç: <strong>{doc.issue_date ? new Date(doc.issue_date).toLocaleDateString('tr-TR') : '-'}</strong></span>
                                      <span>⏳ Bitiş: <strong>{doc.expiry_date ? new Date(doc.expiry_date).toLocaleDateString('tr-TR') : 'Süresiz'}</strong></span>
                                    </div>
                                    {doc.notes && (
                                      <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', fontStyle: 'italic' }}>
                                        Not: {doc.notes}
                                      </div>
                                    )}
                                  </div>

                                  {/* Düzenle & Sil Butonları */}
                                  <div style={{ display: 'flex', gap: '6px', marginLeft: '10px' }}>
                                    <button 
                                      onClick={() => handleOpenEditDoc(emp, doc)}
                                      title="Düzenle"
                                      style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#3b82f6' }}
                                    >
                                      <Edit2 size={13} />
                                    </button>
                                    <button 
                                      onClick={() => handleDeleteDoc(doc.id)}
                                      title="Sil"
                                      style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#dc2626' }}
                                    >
                                      <Trash2 size={13} />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Sağ Kolon: Pozisyon Şartları */}
                        <div>
                          <div style={{ fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '12px', textTransform: 'uppercase' }}>
                            Pozisyonun Zorunlu Kıldığı Belgeler ({emp.required_documents?.length || 0})
                          </div>
                          {(!emp.required_documents || emp.required_documents.length === 0) ? (
                            <div style={{ padding: '14px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', color: '#94a3b8' }}>Bu pozisyon için zorunlu belge kuralı tanımlanmamış.</div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {emp.required_documents.map((req, idx) => {
                                const isOwned = (emp.documents || []).some(d => String(d.document_type_id) === String(req.document_type_id));
                                return (
                                  <div key={idx} style={{ background: isOwned ? '#f0fdf4' : '#fef2f2', border: `1px solid ${isOwned ? '#bbf7d0' : '#fecaca'}`, borderRadius: '8px', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ fontWeight: '600', fontSize: '13px', color: isOwned ? '#15803d' : '#b91c1c' }}>{req.document_name}</div>
                                    <span style={{ fontSize: '11px', fontWeight: '700', color: isOwned ? '#16a34a' : '#dc2626' }}>
                                      {isOwned ? '✓ MEVCUT' : '✗ EKSİK'}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Sayfalama Barı */}
              {totalItems > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderTop: '1px solid #e2e8f0', background: '#fff', fontSize: '13px', color: '#64748b' }}>
                  <div>Toplam <strong>{totalItems}</strong> personelden <strong>{startIndex + 1} - {Math.min(startIndex + itemsPerPage, totalItems)}</strong> arası</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} style={{ display: 'inline-flex', alignItems: 'center', padding: '6px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', background: currentPage === 1 ? '#f8fafc' : '#fff', color: currentPage === 1 ? '#94a3b8' : '#334155', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}><PageLeft size={16} /> Önceki</button>
                    <span style={{ padding: '0 8px', fontWeight: '600', color: '#1e293b' }}>{currentPage} / {totalPages}</span>
                    <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} style={{ display: 'inline-flex', alignItems: 'center', padding: '6px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', background: currentPage === totalPages ? '#f8fafc' : '#fff', color: currentPage === totalPages ? '#94a3b8' : '#334155', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}>Sonraki <PageRight size={16} /></button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* MANUEL BELGE EKLE / DÜZENLE MODALI */}
        {showDocModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
            <div style={{ background: '#fff', borderRadius: '16px', maxWidth: '540px', width: '92%', overflow: 'hidden' }}>
              <div style={{ padding: '18px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '600' }}>
                    {docModalMode === 'add' ? 'Yeni Belge / Sertifika Ekle' : 'Belgeyi Düzenle'}
                  </h3>
                  <p style={{ margin: '3px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                    {activeEmployee?.employee_name} ({activeEmployee?.sicil})
                  </p>
                </div>
                <button onClick={() => setShowDocModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} color="#64748b" /></button>
              </div>

              <form onSubmit={handleSaveDoc} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '5px' }}>Belge / Lisans Türü *</label>
                  <select 
                    value={docFormData.document_type_id} 
                    onChange={(e) => setDocFormData({ ...docFormData, document_type_id: e.target.value })}
                    required
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                  >
                    <option value="">Belge Türü Seçin...</option>
                    {(rulesData.documentTypes || []).map(dt => (
                      <option key={dt.id} value={dt.id}>{dt.name}</option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '5px' }}>Başlangıç Tarihi *</label>
                    <input 
                      type="date" 
                      value={docFormData.issue_date} 
                      onChange={(e) => setDocFormData({ ...docFormData, issue_date: e.target.value })}
                      required
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '5px' }}>Bitiş Tarihi (Opsiyonel)</label>
                    <input 
                      type="date" 
                      value={docFormData.expiry_date} 
                      onChange={(e) => setDocFormData({ ...docFormData, expiry_date: e.target.value })}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '5px' }}>Belge / Sertifika No (Opsiyonel)</label>
                  <input 
                    type="text" 
                    placeholder="Örn: 2026-BELGE-001"
                    value={docFormData.document_number} 
                    onChange={(e) => setDocFormData({ ...docFormData, document_number: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '5px' }}>Sertifika Tanımı / Notlar</label>
                  <textarea 
                    rows={2}
                    placeholder="Belge ile ilgili kurum veya ek açıklama..."
                    value={docFormData.notes} 
                    onChange={(e) => setDocFormData({ ...docFormData, notes: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', resize: 'none' }}
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                  <button type="button" onClick={() => setShowDocModal(false)} style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontSize: '13px' }}>İptal</button>
                  <button type="submit" disabled={docFormLoading} style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', background: '#10b981', color: '#fff', fontWeight: '600', cursor: 'pointer', fontSize: '13px' }}>
                    {docFormLoading ? 'Kaydediliyor...' : 'Kaydet'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* TÜM MEVCUT YETKİNLİK KURALLARINI GÖSTEREN GELİŞMİŞ MODAL */}
        {showRulesModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
            <div style={{ background: '#fff', borderRadius: '16px', maxWidth: '950px', width: '94%', maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Yetkinlik & Belge Zorunluluk Kuralları</h3>
                  <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>Hangi pozisyonun hangi belgeleri zorunlu tuttuğunu eşleştirin ve mevcut tüm kuralları yönetin.</p>
                </div>
                <button onClick={() => setShowRulesModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} color="#64748b" /></button>
              </div>

              <div style={{ padding: '24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* Kural Ekleme Formu */}
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', background: '#f8fafc', padding: '16px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                  <div style={{ flex: 1.2 }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Pozisyon Seçin</label>
                    <select 
                      value={selectedPosition} 
                      onChange={(e) => setSelectedPosition(e.target.value)}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                    >
                      {rulesData.positions.map((pos, idx) => (
                        <option key={idx} value={pos}>{pos}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: 1.5 }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Zorunlu Belge Türü</label>
                    <select 
                      value={selectedDocType} 
                      onChange={(e) => setSelectedDocType(e.target.value)}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                    >
                      <option value="">Belge Türü Seçin...</option>
                      {rulesData.documentTypes.map((dt) => (
                        <option key={dt.id} value={dt.id}>{dt.name}</option>
                      ))}
                    </select>
                  </div>
                  <button 
                    onClick={handleAddRule}
                    disabled={!selectedDocType}
                    style={{ padding: '10px 18px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: selectedDocType ? 'pointer' : 'not-allowed', display: 'inline-flex', alignItems: 'center', gap: '6px', opacity: selectedDocType ? 1 : 0.6, fontSize: '13px' }}
                  >
                    <Plus size={16} /> Kural Ekle
                  </button>
                </div>

                {/* Mevcut Kurallar Tablosu ve Arama */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b' }}>
                      Sistemde Tanımlı Tüm Yetkinlik Kuralları ({rulesData.rules?.length || 0})
                    </div>
                    <div style={{ position: 'relative', width: '280px' }}>
                      <input 
                        type="text"
                        placeholder="Pozisyon veya belge ara..."
                        value={rulesSearch}
                        onChange={(e) => setRulesSearch(e.target.value)}
                        style={{ width: '100%', padding: '7px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '12px' }}
                      />
                    </div>
                  </div>

                  {filteredAllRules.length === 0 ? (
                    <div style={{ padding: '30px', textAlign: 'center', color: '#94a3b8', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1', fontSize: '13px' }}>
                      Kural bulunamadı.
                    </div>
                  ) : (
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden', maxHeight: '350px', overflowY: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
                        <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 1 }}>
                          <tr>
                            <th style={{ padding: '10px 14px', fontWeight: '600', color: '#475569' }}>Pozisyon / Görev</th>
                            <th style={{ padding: '10px 14px', fontWeight: '600', color: '#475569' }}>Zorunlu Belge</th>
                            <th style={{ padding: '10px 14px', textAlign: 'right', fontWeight: '600', color: '#475569' }}>İşlem</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredAllRules.map((rule) => (
                            <tr key={rule.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                              <td style={{ padding: '10px 14px', fontWeight: '600', color: '#1e293b' }}>
                                <Briefcase size={14} color="#64748b" style={{ display: 'inline', marginRight: '6px', verticalAlign: '-2px' }} />
                                {rule.position_title}
                              </td>
                              <td style={{ padding: '10px 14px', color: '#334155' }}>
                                <span style={{ padding: '3px 8px', background: '#eff6ff', color: '#1d4ed8', borderRadius: '6px', fontSize: '12px', fontWeight: '500' }}>
                                  {rule.document_name}
                                </span>
                              </td>
                              <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                                <button 
                                  onClick={() => handleDeleteRule(rule.id)}
                                  title="Kuralı Sil"
                                  style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', cursor: 'pointer', padding: '5px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                >
                                  <Trash2 size={14} /> Sil
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={() => setShowRulesModal(false)} style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', background: '#0f172a', color: '#fff', fontWeight: '600', cursor: 'pointer', fontSize: '13px' }}>Kapat</button>
              </div>
            </div>
          </div>
        )}

        {/* EXCEL IMPORT ONAY MODALI */}
        {showImportModal && previewData && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
            <div style={{ background: '#fff', borderRadius: '16px', maxWidth: '750px', width: '92%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Excel Belge Aktarım Onayı</h3>
                  <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>Dosyadaki kayıtları kontrol edin ve aktarımı onaylayın.</p>
                </div>
                <button onClick={() => setShowImportModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} color="#64748b" /></button>
              </div>

              <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '20px' }}>
                  <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#334155' }}>{previewData.totalRows}</div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>Toplam Satır</div>
                  </div>
                  <div style={{ padding: '16px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0', textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#16a34a' }}>{previewData.validRows}</div>
                    <div style={{ fontSize: '12px', color: '#15803d', marginTop: '2px' }}>Geçerli Belge</div>
                  </div>
                  <div style={{ padding: '16px', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca', textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#dc2626' }}>{previewData.errorRows}</div>
                    <div style={{ fontSize: '12px', color: '#b91c1c', marginTop: '2px' }}>Uyuşmayan Satır</div>
                  </div>
                </div>

                {previewData.preview && (
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', maxHeight: '250px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
                      <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0 }}>
                        <tr>
                          <th style={{ padding: '8px 12px' }}>Sicil</th>
                          <th style={{ padding: '8px 12px' }}>Çalışan</th>
                          <th style={{ padding: '8px 12px' }}>Belge / Lisans</th>
                          <th style={{ padding: '8px 12px' }}>Durum</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.preview.map((row, idx) => {
                          const hasErr = row.errors && row.errors.length > 0;
                          return (
                            <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', background: hasErr ? '#fef2f2' : '#fff' }}>
                              <td style={{ padding: '8px 12px' }}>{row.sicil}</td>
                              <td style={{ padding: '8px 12px' }}>{row.excelEmployeeName}</td>
                              <td style={{ padding: '8px 12px' }}>{row.documentName}</td>
                              <td style={{ padding: '8px 12px' }}>
                                {hasErr ? (
                                  <span style={{ color: '#dc2626', fontSize: '12px' }}>{row.errors[0]}</span>
                                ) : (
                                  <span style={{ color: '#16a34a', fontSize: '12px', fontWeight: '600' }}>✓ Hazır</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button onClick={() => setShowImportModal(false)} style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }} disabled={isImporting}>İptal</button>
                <button 
                  onClick={handleExecuteImport} 
                  disabled={isImporting || previewData.validRows === 0}
                  style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', background: '#e11d48', color: '#fff', fontWeight: '600', cursor: isImporting ? 'not-allowed' : 'pointer' }}
                >
                  {isImporting ? 'Aktarılıyor...' : 'Onayla ve Aktar'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </AdminLayout>
  );
}
